import { useTranslation } from "react-i18next";

import { cn } from "@/utils";

/**
 * Minimum number of survey responses before a rating is published. Must stay in
 * sync with RatingService.MIN_RESPONSES on the backend. The backend already
 * suppresses the value (sends `rating == null`) below this threshold; the count
 * check here is a defensive fallback so the UI never renders a half-baked score
 * if an unsuppressed value ever slips through.
 */
export const MIN_RATING_RESPONSES = 5;

type StarRatingProps = {
  /** Published 1-5 rating, or null/undefined when suppressed ("New"). */
  value?: number | null;
  /** Number of responses backing the rating; used for the suppression fallback. */
  count?: number | null;
  /** Show the numeric value (e.g. "4.3") next to the stars. */
  showValue?: boolean;
  /** Show the response count (e.g. "(12)") next to the stars. */
  showCount?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
};

const SIZE_CLASS: Record<NonNullable<StarRatingProps["size"]>, string> = {
  sm: "text-sm",
  md: "text-base",
  lg: "text-2xl",
};

/**
 * Read-only 1-5 star rating. Renders "New" when the subject hasn't reached the
 * response threshold yet. Fractional values render a partial star via a clipped
 * gold overlay on top of the muted base row.
 */
export default function StarRating({
  value,
  count,
  showValue = true,
  showCount = false,
  size = "md",
  className,
}: StarRatingProps) {
  const { t } = useTranslation("common");

  const published =
    value != null &&
    value > 0 &&
    (count == null || count >= MIN_RATING_RESPONSES);

  const sizeClass = SIZE_CLASS[size];

  if (!published) {
    return (
      <span
        className={cn(
          "inline-flex items-center rounded-full bg-fog px-2 py-0.5 font-medium text-slate",
          sizeClass === "text-2xl" ? "text-xs" : "text-[11px]",
          className,
        )}
        title={t("rating.newTooltip", "Not enough ratings yet")}
      >
        {t("rating.new", "New")}
      </span>
    );
  }

  const clamped = Math.max(0, Math.min(5, value));
  const fillPct = (clamped / 5) * 100;

  return (
    <span
      className={cn("inline-flex items-center gap-1", sizeClass, className)}
      title={`${clamped.toFixed(1)} / 5`}
      aria-label={`${clamped.toFixed(1)} out of 5 stars`}
    >
      <span className="relative inline-block leading-none">
        {/* muted base row */}
        <span className="text-[#AEAEB2]/50">★★★★★</span>
        {/* gold overlay clipped to the fill percentage */}
        <span
          className="absolute inset-0 overflow-hidden whitespace-nowrap text-yellow-400"
          style={{ width: `${fillPct}%` }}
        >
          ★★★★★
        </span>
      </span>
      {showValue && (
        <span className="font-semibold text-garnet">{clamped.toFixed(1)}</span>
      )}
      {showCount && count != null && (
        <span className="text-slate">({count})</span>
      )}
    </span>
  );
}
